import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MuxPlayer from '@mux/mux-player-react/index';
import { useAuth } from '../contexts/AuthContext';
import {
  getLiveStream,
  joinLiveStream,
  leaveLiveStream,
  watchLiveStream,
} from '../services/liveStreamService';
import { LiveStream as LiveStreamType } from '../types/video';
import './Pages.css';

export const LiveViewer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const streamId = searchParams.get('streamId');

  const [stream, setStream] = useState<LiveStreamType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initializeViewer();
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (streamId) {
      const unsubscribe = watchLiveStream(streamId, (updatedStream) => {
        if (updatedStream) {
          setStream(updatedStream);
          if (updatedStream.status === 'ended') {
            alert('A live foi encerrada pelo host.');
            navigate(-1);
          }
        }
      });
      return unsubscribe;
    }
  }, [streamId]);

  const initializeViewer = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!streamId) {
        throw new Error('ID da live não fornecido');
      }

      if (!user?.uid) {
        throw new Error('Usuário não autenticado');
      }

      // Buscar informações da live
      const liveStream = await getLiveStream(streamId);
      if (!liveStream) {
        throw new Error('Live não encontrada');
      }

      setStream(liveStream);

      // Entrar na live como viewer
      await joinLiveStream(streamId, user.uid);
      setLoading(false);

    } catch (error: any) {
      console.error('Erro ao inicializar viewer:', error);
      setError(error.message || 'Erro ao entrar na live');
      setLoading(false);
    }
  };

  const cleanup = async () => {
    try {
      if (streamId && user?.uid) {
        await leaveLiveStream(streamId, user.uid);
      }
    } catch (error) {
      console.error('Erro ao limpar recursos:', error);
    }
  };

  if (loading) {
    return (
      <div className="video-call-container">
        <div className="video-call-loading">
          <div className="spinner"></div>
          <p>Entrando na live...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-call-container">
        <div className="video-call-error">
          <p>{error}</p>
          <button onClick={initializeViewer}>Tentar Novamente</button>
          <button onClick={() => navigate(-1)}>Voltar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="live-stream-container">
      {/* Player Mux */}
      {stream?.muxPlaybackId ? (
        <MuxPlayer
          streamType="live"
          playbackId={stream.muxPlaybackId}
          metadata={{
            video_id: stream.id,
            video_title: stream.title,
            viewer_user_id: user?.uid,
          }}
          style={{ height: '100%', width: '100%' }}
          autoPlay
        />
      ) : (
        <div className="video-call-placeholder">
          <div className="spinner"></div>
          <p>Aguardando transmissão...</p>
        </div>
      )}

      {/* Informações da live (Overlay) */}
      {stream && (
        <div className="live-stream-info">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />
            <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>AO VIVO</span>
          </div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
            {stream.title}
          </h2>
          {stream.description && (
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#fff', opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
              {stream.description}
            </p>
          )}
          <p style={{ margin: '0', fontSize: '14px', color: '#fff', opacity: 0.8, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
            {stream.viewerCount || 0} visualizações
          </p>
        </div>
      )}

      {/* Botão de fechar */}
      <button
        onClick={() => navigate(-1)}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'rgba(0,0,0,0.5)',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          color: '#fff',
          fontSize: '20px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ✕
      </button>
    </div>
  );
};
